import { useEffect, useState } from "react";
import { DashboardSearch } from "@/components/DashboardSearch";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { DASH } from "@/constants/testIds";
import { toast } from "sonner";
import { Users, UserRound, Wrench, Building2, Send, Mail, ArrowUpRight, Star, Share2, Sparkles, Clock, Check, X, Undo2, Heart, Flame, Bookmark, Trash2, Link2, Users2, Plus, Zap, AlertTriangle, CheckCircle2, Moon, Timer, MapPin } from "lucide-react";
import { useSectionTheme } from "@/hooks/useSectionTheme";

// Session Z89 — live turnover strip. Shows every cleaning task currently
// `in_progress` with the running timer since "I'm here" was tapped. Polls
// every 30 seconds so the office sees arrivals surface in near-real-time.
function LiveTurnoverStrip() {
  const [rows, setRows] = useState([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/turnovers/live")
      .then((r) => { if (!cancelled) setRows(r.data || []); })
      .catch(() => {});
    load();
    const refresh = setInterval(load, 30 * 1000);
    const timer = setInterval(() => setTick((n) => n + 1), 60 * 1000);
    return () => { cancelled = true; clearInterval(refresh); clearInterval(timer); };
  }, []);
  if (!rows.length) return null;
  const _void = tick; // keeps setTick in deps-less loop valid for lint
  return (
    <section
      data-testid="dashboard-live-turnover-strip"
      className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-50 p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
        </span>
        <div className="ns-label text-amber-800">Live turnovers · in progress</div>
        <span className="text-xs text-slate-500">
          {rows.length} crew on-site
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {rows.map((r) => {
          const minutes = r.started_at
            ? Math.max(0, Math.round((Date.now() - new Date(r.started_at).getTime()) / 60000))
            : null;
          return (
            <div
              key={r.task_id}
              data-testid={`dashboard-live-turnover-${r.task_id}`}
              className="shrink-0 min-w-[220px] rounded-xl bg-white border border-amber-100 px-3 py-2.5 shadow-sm"
            >
              <div className="text-xs font-medium text-slate-900 truncate">{r.property_name}</div>
              {r.property_address && (
                <div className="text-[11px] text-slate-500 truncate">{r.property_address}</div>
              )}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100 text-[10px]">
                  <Timer className="w-3 h-3" /> {minutes ?? "—"} min
                </span>
                {r.started_by_name && (
                  <span className="text-[10px] text-slate-500 truncate max-w-[120px]">
                    {r.started_by_name}
                  </span>
                )}
                {r.geofence_mismatch && (
                  <span
                    data-testid={`dashboard-live-mismatch-${r.task_id}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100 text-[10px]"
                    title="Recorded GPS >200 m from the property address"
                  >
                    <MapPin className="w-3 h-3" /> off-site
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Session Z92 — fastest / slowest crews this week tile. Reads the backend
// leaderboard aggregation and renders two side-by-side columns. Hidden when
// no completed turnovers happened in the trailing 7 days.
function RevpanTile() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/finance/revpan").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data || !data.rows.length) return null;
  const top = data.rows.slice(0, 6);
  const max = Math.max(...top.map((r) => r.revpan), 1);
  return (
    <section data-testid="dashboard-revpan" className="dash-wash dash-sea p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="ns-label">Revenue per available night · {data.month}</div>
        <span className="text-[11px] text-slate-400">{data.nights_in_month} nights</span>
      </div>
      <ul className="space-y-2">
        {top.map((r) => (
          <li key={r.property_id} data-testid={`dashboard-revpan-row-${r.property_id}`}>
            <div className="flex items-center justify-between text-xs">
              <span className="truncate text-slate-800 pr-3">{r.property_name}</span>
              <span className="font-mono whitespace-nowrap">£{r.revpan.toFixed(0)}<span className="text-slate-400"> · {r.occupancy_pct}% occ</span></span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden">
              <div className="h-full bg-teal-600 rounded-full transition-[width] duration-700" style={{ width: `${(r.revpan / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ProfitLeaderboardTile() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/finance/profit-leaderboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data || !data.count) return null;
  const gbp = (n) => `${n < 0 ? "−" : ""}£${Math.abs(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
  const Col = ({ title, rows, tone }) => (
    <div>
      <div className={`text-[10px] uppercase tracking-wider mb-1 ${tone === "best" ? "text-emerald-700" : "text-rose-700"}`}>{title}</div>
      {rows.length ? (
        <ul>{rows.map((r) => (
          <li key={r.property_id} data-testid={`dashboard-profit-${tone}-${r.property_id}`} className="flex items-center justify-between gap-3 py-1.5 border-b last:border-b-0 border-slate-100">
            <span className="text-xs text-slate-800 truncate">{r.property_name}</span>
            <span className={`font-mono text-xs ${r.net < 0 ? "text-rose-700" : "text-emerald-700"}`}>{gbp(r.net)}</span>
          </li>
        ))}</ul>
      ) : <div className="text-xs text-slate-400 italic">Not enough properties yet</div>}
    </div>
  );
  return (
    <section data-testid="dashboard-profit-leaderboard" className="dash-wash dash-coral p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="ns-label">Profit leaderboard · {data.month}</div>
        <Link to="/finance" className="text-[11px] text-teal-700 hover:underline">Open finance</Link>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <Col title="Best" rows={data.best} tone="best" />
        <Col title="Worst" rows={data.worst} tone="worst" />
      </div>
    </section>
  );
}

function LeaseExpiryTile() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/leases/expiring?days=90").then((r) => setRows(r.data.rows)).catch(() => {});
  useEffect(() => { load(); }, []);
  const renew = async (r) => {
    if (!window.confirm(`Extend ${r.property_name} lease by 12 months?`)) return;
    await api.post(`/leases/${r.property_id}/renew`);
    toast.success("Lease renewed for 12 months");
    load();
  };
  if (!rows || !rows.length) return null;
  const tone = (d) => d < 0 ? "bg-rose-50 text-rose-700 border-rose-100" : d <= 30 ? "bg-amber-50 text-amber-800 border-amber-100" : "bg-slate-50 text-slate-700 border-slate-100";
  return (
    <section data-testid="dashboard-lease-expiry" className="dash-wash dash-sun p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="ns-label">Leases expiring · next 90 days</div>
        <Link to="/properties" className="text-[11px] text-teal-700 hover:underline">{rows.length} lease{rows.length === 1 ? "" : "s"}</Link>
      </div>
      <ul className="grid md:grid-cols-2 gap-x-6">
        {rows.map((r) => (
          <li key={r.property_id} data-testid={`dashboard-lease-row-${r.property_id}`} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100">
            <span className="text-xs text-slate-800 truncate">{r.property_name}</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] border whitespace-nowrap ${tone(r.days_left)}`}>
              {r.end_date} · {r.days_left < 0 ? `${-r.days_left}d overdue` : `${r.days_left}d`}
            </span>
            <button onClick={() => renew(r)} data-testid={`dashboard-lease-renew-${r.property_id}`} className="text-[10px] text-teal-700 hover:text-teal-900 transition-colors">Renew</button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CrewLeaderboardTile() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/turnovers/crew-leaderboard")
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);
  if (!data || !data.crew_count) return null;
  const Row = ({ r, tone }) => (
    <li
      key={r.started_by_name}
      data-testid={`dashboard-crew-row-${tone}`}
      className="flex items-center justify-between gap-3 py-1.5 border-b last:border-b-0 border-slate-100"
    >
      <span className="text-xs text-slate-800 truncate">{r.started_by_name}</span>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] whitespace-nowrap ${tone === "fast" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-rose-50 text-rose-700 border border-rose-100"}`}>
        {r.avg_minutes} min · {r.turnovers}
      </span>
    </li>
  );
  return (
    <section
      data-testid="dashboard-crew-leaderboard"
      className="dash-wash dash-teal p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="ns-label">Crew momentum · last 7 days</div>
        <span className="text-[11px] text-slate-500">
          Portfolio avg {data.portfolio_avg_minutes} min · {data.crew_count} crew
        </span>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 mb-1">Fastest</div>
          {data.fastest.length ? (
            <ul>{data.fastest.map((r) => <Row key={r.started_by_name} r={r} tone="fast" />)}</ul>
          ) : (
            <div className="text-xs text-slate-400 italic">No completed turnovers yet</div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-rose-700 mb-1">Slowest</div>
          {data.slowest.length ? (
            <ul>{data.slowest.map((r) => <Row key={r.started_by_name} r={r} tone="slow" />)}</ul>
          ) : (
            <div className="text-xs text-slate-400 italic">No completed turnovers yet</div>
          )}
        </div>
      </div>
    </section>
  );
}

// Session Z67 — tiny health pill that reads the daily-morning Stripe
// webhook-health classification and tints itself green/amber/red/slate.
function StripeHealthBadge() {
  const [health, setHealth] = useState(null);
  useEffect(() => {
    api.get("/stripe/smoke-test/health")
      .then((r) => setHealth(r.data))
      .catch(() => setHealth(null));
  }, []);
  if (!health || !health.state) return null;
  const palette = {
    green: { bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-100", icon: CheckCircle2, label: "healthy" },
    amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-100", icon: AlertTriangle, label: "flaky" },
    red: { bg: "bg-red-50", text: "text-red-700", border: "border-red-100", icon: AlertTriangle, label: "broken" },
    unknown: { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-200", icon: Zap, label: "no data" },
  };
  const p = palette[health.state] || palette.unknown;
  const Icon = p.icon;
  const total = (health.webhook_count || 0) + (health.fallback_count || 0);
  return (
    <Link
      to="/stripe-smoke-test"
      data-testid="dashboard-stripe-health-badge"
      className={`inline-flex items-center gap-1.5 rounded-full border ${p.border} ${p.bg} ${p.text} px-3 py-1 text-[11px] uppercase tracking-wider hover:opacity-80 transition-opacity`}
      title={health.headline || `Stripe ${p.label} · ${total} smoke tests in last 7d`}
    >
      <Icon className="w-3 h-3" />
      Stripe {p.label}
      {health.fallback_count > 0 && (
        <span className="font-mono normal-case tracking-normal opacity-70">
          · {health.fallback_count}/{total} fallback
        </span>
      )}
    </Link>
  );
}

// Session Z83 — Guest dark-mode adoption chip. Minimal sparkline next to the
// Stripe health badge so Dionne sees adoption of the night palette at a glance.
function GuestDarkAdoptionChip() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/metrics/guest-dark-adoption")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);
  if (!data) return null;
  const dark = data.dark_guests || 0;
  const total = data.total_guests || 0;
  // Keep the chip visible even with 0 — it reassures Dionne the pipeline is wired.
  const empty = total === 0;
  const trend = (data.daily || []).map((d) => d.dark || 0);
  const peak = Math.max(1, ...trend);
  return (
    <div
      data-testid="dashboard-guest-dark-chip"
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] uppercase tracking-wider ${
        empty
          ? "bg-slate-50 border-slate-200 text-slate-500"
          : "bg-indigo-50 border-indigo-100 text-indigo-700"
      }`}
      title={empty
        ? "No guests have touched the dark toggle in the past 7 days yet"
        : `${dark} of ${total} guests browsing in dark mode (last 7d) · auto:${data.auto_guests} light:${data.light_guests}`}
    >
      <Moon className="w-3 h-3" />
      <span data-testid="dashboard-guest-dark-count">
        {dark} guest{dark === 1 ? "" : "s"} · dark
      </span>
      {!empty && (
        <svg
          width="42" height="12" viewBox="0 0 42 12"
          data-testid="dashboard-guest-dark-sparkline"
          className="opacity-70"
          aria-hidden="true"
        >
          {trend.map((v, i) => {
            const h = Math.max(1, (v / peak) * 10);
            return (
              <rect
                key={i}
                x={i * 6}
                y={12 - h}
                width={4}
                height={h}
                rx={1}
                fill="currentColor"
              />
            );
          })}
        </svg>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, tone = "teal", testid }) {
  const tones = {
    teal: ["dash-teal", "bg-teal-600 text-white", "text-teal-950"],
    navy: ["dash-sea", "bg-sky-600 text-white", "text-sky-950"],
    amber: ["dash-sun", "bg-amber-500 text-white", "text-amber-950"],
    plum: ["dash-coral", "bg-orange-500 text-white", "text-orange-950"],
  };
  const [wash, chip, ink] = tones[tone] || tones.teal;
  return (
    <div data-testid={testid} className={`ns-card dash-wash ${wash} p-6 flex flex-col justify-between`}>
      <div className="flex items-start justify-between">
        <div className="ns-label">{label}</div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${chip}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="mt-6">
        <div className={`font-serif text-4xl leading-none ${ink}`}>{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const sectionTheme = useSectionTheme("dashboard");
  const [stats, setStats] = useState(null);
  const [surveys, setSurveys] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [turnovers, setTurnovers] = useState([]);
  const [refunds, setRefunds] = useState(null);
  const [topWishlisted, setTopWishlisted] = useState([]);
  const [cleaningLeaderboard, setCleaningLeaderboard] = useState([]);
  const [exportFilter, setExportFilter] = useState("all");
  const [exportPresets, setExportPresets] = useState([]);
  const [teamPresets, setTeamPresets] = useState([]);
  const [presetsMenuOpen, setPresetsMenuOpen] = useState(false);

  const loadPresets = () => {
    api.get("/wishlist/export-presets")
      .then((r) => setExportPresets(r.data?.presets || []))
      .catch(() => {});
    api.get("/wishlist/team-export-presets")
      .then((r) => setTeamPresets(r.data?.presets || []))
      .catch(() => {});
  };

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/surveys/summary").then((r) => setSurveys(r.data)).catch(() => {});
    api.get("/referrals/leaderboard").then((r) => setLeaderboard(r.data || [])).catch(() => {});
    api.get("/turnovers/today").then((r) => setTurnovers(r.data || [])).catch(() => {});
    api.get("/dashboard/refunds").then((r) => setRefunds(r.data)).catch(() => {});
    api.get("/wishlist/top-wishlisted?limit=5").then((r) => setTopWishlisted(r.data || [])).catch(() => {});
    api.get("/cleaning-companies/leaderboard").then((r) => setCleaningLeaderboard(r.data || [])).catch(() => {});
    loadPresets();
    // Session Z28 — pick up a shared preset from a teammate's link
    const params = new URLSearchParams(window.location.search);
    const shareTok = params.get("share_preset");
    if (shareTok) {
      api.post("/wishlist/export-presets/import", { token: shareTok })
        .then((r) => {
          if (r.data?.already_exists) {
            toast.info(`Preset "${r.data.name}" already saved — no change`);
          } else {
            toast.success(
              `Preset "${r.data.name}" saved${r.data.shared_by ? ` (shared by ${r.data.shared_by})` : ""}`,
            );
            loadPresets();
          }
        })
        .catch((err) => toast.error(err?.response?.data?.detail || "Share link is invalid or expired"))
        .finally(() => {
          params.delete("share_preset");
          const qs = params.toString();
          window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
        });
    }
  }, []);

  const exportPortfolio = async (filter) => {
    try {
      const resp = await api.get(
        `/wishlist/watchers-all?format=csv&filter=${filter}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const match = /filename="([^"]+)"/.exec(resp.headers?.["content-disposition"] || "");
      a.download = match ? match[1] : `watchers_portfolio_${filter}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed — try again");
    }
  };

  const savePresetPrompt = async () => {
    // eslint-disable-next-line no-alert
    const name = window.prompt(`Save "${exportFilter}" filter as a preset. Name it:`);
    if (!name || !name.trim()) return;
    try {
      const r = await api.post("/wishlist/export-presets", {
        name: name.trim(), filter: exportFilter,
      });
      setExportPresets((prev) => [...prev, r.data]);
      toast.success(`Preset "${r.data.name}" saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save preset");
    }
  };

  const sharePreset = async (p) => {
    try {
      const r = await api.post(`/wishlist/export-presets/${p.id}/share`);
      const url = `${window.location.origin}/dashboard?share_preset=${encodeURIComponent(r.data.token)}`;
      await navigator.clipboard.writeText(url);
      toast.success(`Share link copied — expires in ${r.data.expires_days} days`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create share link");
    }
  };

  const publishPreset = async (p) => {
    try {
      const r = await api.post(`/wishlist/export-presets/${p.id}/publish`);
      if (r.data?.already_published) {
        toast.info(`"${p.name}" is already in the team library`);
      } else {
        toast.success(`Published "${p.name}" to the team library`);
      }
      api.get("/wishlist/team-export-presets")
        .then((rr) => setTeamPresets(rr.data?.presets || []))
        .catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not publish");
    }
  };

  const adoptTeamPreset = async (tp) => {
    try {
      const r = await api.post(`/wishlist/team-export-presets/${tp.id}/adopt`);
      if (r.data?.already_exists) {
        toast.info(`"${tp.name}" is already in your presets`);
      } else {
        toast.success(`"${tp.name}" adopted from ${r.data.imported_from || "team"}`);
        setExportPresets((prev) => [...prev, r.data]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not adopt preset");
    }
  };

  const retractTeamPreset = async (tp) => {
    try {
      await api.delete(`/wishlist/team-export-presets/${tp.id}`);
      setTeamPresets((prev) => prev.filter((x) => x.id !== tp.id));
      toast.success(`"${tp.name}" removed from team library`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not retract");
    }
  };

  const runPreset = (p) => {
    setExportFilter(p.filter);
    setPresetsMenuOpen(false);
    exportPortfolio(p.filter);
    // Session Z30 — bump usage counter so owners see which presets earn their keep.
    api.post(`/wishlist/export-presets/${p.id}/run`)
      .then((r) => setExportPresets((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, run_count: (x.run_count || 0) + 1 } : x)),
      ))
      .catch(() => {});
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/wishlist/export-presets/${id}`);
      setExportPresets((prev) => prev.filter((p) => p.id !== id));
      toast.success("Preset removed");
    } catch {
      toast.error("Could not delete preset");
    }
  };

  const t = stats?.totals || {};

  return (
    <Layout
      title="Your quiet command centre"
      subtitle="A snapshot of every guest, contractor and campaign flowing through Newport Stays."
    >
      <div data-testid={DASH.root} className="space-y-8 dash-bg">
        <DashboardSearch />
        <LiveTurnoverStrip />
        <CrewLeaderboardTile />
        <LeaseExpiryTile />
        <div className="grid lg:grid-cols-2 gap-4">
          <ProfitLeaderboardTile />
          <RevpanTile />
        </div>
        <div className="flex items-center justify-end gap-3 -mb-4">
          <GuestDarkAdoptionChip />
          <StripeHealthBadge />
        </div>
        <section className={`ns-section ns-section-${sectionTheme}`} data-section-theme={sectionTheme}>
          <div className="flex items-center gap-3 mb-5">
            <div className={`ns-rail ns-rail-${sectionTheme} self-stretch min-h-[2.5rem]`} />
            <div className="flex-1 min-w-0">
              <div className="ns-label text-teal-800">Portfolio pulse</div>
              <div className="font-serif text-xl text-slate-900 mt-0.5">At-a-glance headcount</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <StatCard testid={DASH.cardContacts} label="Contacts" value={t.contacts ?? "—"} icon={Users} tone="navy" />
            <StatCard testid={DASH.cardGuests} label="Past guests" value={t.guests ?? "—"} icon={UserRound} tone="teal" />
            <StatCard testid={DASH.cardContractors} label="Contractors" value={t.contractors ?? "—"} icon={Wrench} tone="amber" />
            <StatCard label="Suppliers · Owners" value={`${t.suppliers ?? 0} · ${t.owners ?? 0}`} icon={Users} tone="plum" />
            <StatCard testid={DASH.cardProperties} label="Properties" value={t.properties ?? "—"} sub="Serviced units" icon={Building2} tone="navy" />
            <StatCard testid={DASH.cardCampaigns} label="Campaigns" value={t.campaigns ?? "—"} sub={`${t.emails_sent ?? 0} emails delivered`} icon={Send} tone="teal" />
          </div>
        </section>

        {refunds && refunds.count > 0 && (
          <section data-testid="dashboard-refunds-tile" className="ns-section ns-section-amber">
            <div className="flex items-start gap-3 mb-4">
              <div className="ns-rail ns-rail-amber self-stretch min-h-[3rem]" />
              <div className="flex-1 min-w-0">
                <div className="ns-label text-amber-800">Refunds this month</div>
                <div className="font-serif text-3xl mt-1">
                  £{refunds.total.toFixed(2)}
                  <span className="ml-3 text-sm text-slate-400 tracking-wider">
                    {refunds.count} refund{refunds.count === 1 ? "" : "s"} · {refunds.month}
                  </span>
                </div>
              </div>
              <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
                <Undo2 className="w-4 h-4" />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {refunds.by_reason.map((r) => (
                <div
                  key={r.reason}
                  data-testid={`refund-reason-${r.reason}`}
                  className="bg-white/80 border border-amber-200 rounded-full px-3.5 py-1.5 text-xs shadow-sm"
                >
                  <span className="tracking-wider uppercase text-amber-800">
                    {r.reason.replace(/_/g, " ")}
                  </span>
                  <span className="ml-2 font-medium text-slate-800">£{r.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section data-testid="dashboard-cleaning-leaderboard" className="ns-section ns-section-plum">
          <div className="flex items-start justify-between mb-4 gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="ns-rail ns-rail-plum self-stretch min-h-[2.5rem]" />
              <div className="flex-1 min-w-0">
                <div className="ns-label text-violet-800">Cleaning company leaderboard</div>
                <div className="font-serif text-xl mt-1">Top-rated suppliers</div>
                <div className="text-xs text-slate-500 mt-1">
                  Ranked by average star rating across every turnover — highest performers first.
                </div>
              </div>
            </div>
            <div className="w-9 h-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
          </div>
          {cleaningLeaderboard.length === 0 ? (
            <div
              data-testid="dashboard-cleaning-leaderboard-empty"
              className="text-sm text-slate-400 py-6 text-center italic"
            >
              No rated turnovers yet — star-rate a completed cleaning task to seed the ranking.
            </div>
          ) : (
            <div className="space-y-1">
              {cleaningLeaderboard.map((c, i) => (
                <div
                  key={c.id}
                  data-testid={`dashboard-cleaning-leaderboard-row-${c.id}`}
                  className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white p-3 hover:border-teal-200 transition"
                >
                  <div className="w-6 text-center text-xs font-medium text-slate-400">#{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <span key={n} className={n <= Math.round(c.avg_rating) ? "text-amber-500" : "text-slate-200"}>★</span>
                      ))}
                      <span className="ml-1 text-slate-500">{c.avg_rating.toFixed(1)}</span>
                      <span className="ml-1 text-slate-400">· {c.rating_count} rating{c.rating_count === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {topWishlisted.length > 0 && (
          <section data-testid="dashboard-top-wishlisted" className="ns-card dash-wash dash-coral p-6">
            <div className="flex items-start justify-between mb-4 gap-4">
              <div className="flex-1 min-w-0">
                <div className="ns-label">Top wishlisted units</div>
                <div className="font-serif text-xl mt-1">
                  {topWishlisted.length === 1 ? "1 unit" : `${topWishlisted.length} units`} guests are watching
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Two or more guests on a wishlist = a hot unit. Consider raising the nightly rate.
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="w-9 h-9 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center">
                  <Heart className="w-4 h-4" />
                </div>
                <div className="flex items-center gap-1 relative">
                  <select
                    data-testid="dashboard-top-wishlisted-filter"
                    value={exportFilter}
                    onChange={(e) => setExportFilter(e.target.value)}
                    className="h-7 rounded-full border border-slate-200 bg-white px-2 text-[10px] focus:outline-none focus:ring-1 focus:ring-teal-400"
                    title="Segment before you export"
                  >
                    <option value="all">All watchers</option>
                    <option value="weekly">Weekly digest only</option>
                    <option value="hot">Hot units only</option>
                  </select>
                  <button
                    type="button"
                    data-testid="dashboard-top-wishlisted-export"
                    onClick={() => exportPortfolio(exportFilter)}
                    className="text-[10px] rounded-full bg-teal-700 text-white px-2.5 py-1 hover:bg-teal-800 transition whitespace-nowrap"
                    title="Export the segmented watcher list as CSV"
                  >
                    Export
                  </button>
                  <button
                    type="button"
                    data-testid="dashboard-export-presets-btn"
                    onClick={() => setPresetsMenuOpen((v) => !v)}
                    className={`inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-1 border transition whitespace-nowrap ${
                      presetsMenuOpen
                        ? "bg-amber-50 border-amber-200 text-amber-800"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-stone-50"
                    }`}
                    title="Saved filter presets — one-click exports"
                  >
                    <Bookmark className="w-3 h-3" /> Presets
                    {exportPresets.length > 0 && (
                      <span className="ml-0.5 text-[9px] text-slate-400">({exportPresets.length})</span>
                    )}
                  </button>
                  {presetsMenuOpen && (
                    <div
                      data-testid="dashboard-export-presets-menu"
                      className="absolute right-0 top-9 z-20 w-64 rounded-xl bg-white border border-slate-200 shadow-lg p-2"
                    >
                      <div className="ns-label text-[9px] text-slate-500 px-2 pt-1 pb-2">Saved presets</div>
                      {exportPresets.length === 0 ? (
                        <div className="text-[11px] text-slate-400 italic px-2 py-2">
                          No presets yet — save the current filter below.
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          {exportPresets.map((p) => (
                            <div
                              key={p.id}
                              data-testid={`dashboard-export-preset-${p.id}`}
                              className="flex items-center gap-2 rounded-lg hover:bg-stone-50 px-2 py-1.5"
                            >
                              <button
                                type="button"
                                onClick={() => runPreset(p)}
                                data-testid={`dashboard-export-preset-run-${p.id}`}
                                className="flex-1 min-w-0 text-left"
                                title={`Export ${p.filter} watchers`}
                              >
                                <div className="text-xs font-medium truncate flex items-center gap-1.5">
                                  {p.name}
                                  {(p.run_count || 0) > 0 && (
                                    <span
                                      data-testid={`dashboard-export-preset-runs-${p.id}`}
                                      className="text-[9px] text-teal-700 bg-teal-50 rounded-full px-1.5 py-0.5"
                                    >
                                      ×{p.run_count}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[10px] text-slate-400 truncate">
                                  {p.filter}
                                  {p.imported_from && (
                                    <span className="ml-1 text-indigo-500">· from {p.imported_from}</span>
                                  )}
                                </div>
                              </button>
                              <button
                                type="button"
                                onClick={() => publishPreset(p)}
                                data-testid={`dashboard-export-preset-publish-${p.id}`}
                                className="text-slate-300 hover:text-indigo-600 transition"
                                title="Publish to team library"
                              >
                                <Users2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => sharePreset(p)}
                                data-testid={`dashboard-export-preset-share-${p.id}`}
                                className="text-slate-300 hover:text-teal-600 transition"
                                title="Copy share link (expires in 30 days)"
                              >
                                <Link2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => deletePreset(p.id)}
                                data-testid={`dashboard-export-preset-del-${p.id}`}
                                className="text-slate-300 hover:text-rose-500 transition"
                                title="Remove preset"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {teamPresets.length > 0 && (
                        <>
                          <div className="ns-label text-[9px] text-indigo-500 px-2 pt-3 pb-1 flex items-center gap-1">
                            <Users2 className="w-3 h-3" /> Team library
                          </div>
                          <div className="space-y-0.5">
                            {teamPresets.map((tp) => {
                              const alreadyMine = exportPresets.some(
                                (p) => (p.name || "").toLowerCase() === (tp.name || "").toLowerCase(),
                              );
                              return (
                                <div
                                  key={tp.id}
                                  data-testid={`dashboard-team-preset-${tp.id}`}
                                  className="flex items-center gap-2 rounded-lg hover:bg-indigo-50/40 px-2 py-1.5"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-medium truncate flex items-center gap-1.5">
                                      {tp.name}
                                      {(tp.adopt_count || 0) > 0 && (
                                        <span
                                          data-testid={`dashboard-team-preset-adopts-${tp.id}`}
                                          className="text-[9px] text-indigo-700 bg-indigo-50 rounded-full px-1.5 py-0.5"
                                          title={`${tp.adopt_count} teammates adopted this`}
                                        >
                                          {tp.adopt_count} adopted
                                        </span>
                                      )}
                                      {(tp.run_count || 0) > 0 && (
                                        <span
                                          data-testid={`dashboard-team-preset-runs-${tp.id}`}
                                          className="text-[9px] text-teal-700 bg-teal-50 rounded-full px-1.5 py-0.5"
                                          title={`Exported ${tp.run_count} times`}
                                        >
                                          ×{tp.run_count}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[10px] text-slate-400 truncate">
                                      {tp.filter}
                                      {tp.published_by && (
                                        <span className="ml-1 text-indigo-500">· by {tp.published_by}</span>
                                      )}
                                    </div>
                                  </div>
                                  {alreadyMine ? (
                                    <span
                                      data-testid={`dashboard-team-preset-adopted-${tp.id}`}
                                      className="text-[9px] text-emerald-600 uppercase tracking-wider"
                                    >
                                      In your list
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => adoptTeamPreset(tp)}
                                      data-testid={`dashboard-team-preset-adopt-${tp.id}`}
                                      className="text-slate-300 hover:text-emerald-600 transition"
                                      title="Adopt into your own presets"
                                    >
                                      <Plus className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => retractTeamPreset(tp)}
                                    data-testid={`dashboard-team-preset-retract-${tp.id}`}
                                    className="text-slate-300 hover:text-rose-500 transition"
                                    title="Retract from team library (publisher/owner only)"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                      {false && (
                        <div />
                      )}
                      <button
                        type="button"
                        onClick={savePresetPrompt}
                        data-testid="dashboard-export-preset-save"
                        className="mt-2 w-full text-[11px] rounded-lg bg-teal-700 text-white px-3 py-1.5 hover:bg-teal-800 transition"
                      >
                        Save current ({exportFilter}) as preset
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {topWishlisted.map((w, idx) => (
                <Link
                  key={w.property_id}
                  to={`/calendar?highlight=${w.property_id}`}
                  data-testid={`dashboard-top-wishlisted-${w.property_id}`}
                  className="flex items-center gap-3 rounded-xl bg-stone-50 px-4 py-3 hover:bg-teal-50/60 hover:ring-1 hover:ring-teal-200 transition group"
                  title="Open in Calendar to adjust rates"
                >
                  <div className="w-6 text-center ns-label text-slate-400">#{idx + 1}</div>
                  <Building2 className="w-4 h-4 text-teal-700 shrink-0" />
                  <div className="flex-1 min-w-0 truncate font-medium">{w.property_name}</div>
                  {w.hot && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium px-2 py-0.5">
                      <Flame className="w-3 h-3" /> Hot
                    </span>
                  )}
                  <div className="text-sm text-slate-600 font-medium w-20 text-right">
                    {w.watchers} watcher{w.watchers === 1 ? "" : "s"}
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-teal-700 shrink-0" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="grid lg:grid-cols-2 gap-6">
          <div data-testid="dashboard-turnover-tile" className="ns-card dash-wash dash-teal p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="ns-label">Today&apos;s turnovers</div>
                <div className="font-serif text-xl mt-1">
                  {turnovers.length === 0
                    ? "No checkouts today"
                    : `${turnovers.length} checkout${turnovers.length === 1 ? "" : "s"} to cover`}
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                  {turnovers.filter((t) => t.cleaner_response === "yes").length} confirmed
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-amber-600" />
                  {turnovers.filter((t) => t.cleaner_notified && !t.cleaner_response).length} pending
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <X className="w-3.5 h-3.5 text-red-600" />
                  {turnovers.filter((t) => t.cleaner_response === "no").length} declined
                </span>
              </div>
            </div>
            {turnovers.length === 0 ? (
              <div className="text-sm text-slate-400 py-8 text-center">
                A relaxed morning — no departures scheduled for today.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {turnovers.map((t) => {
                  const status = t.cleaner_response === "yes"
                    ? { color: "emerald", icon: Check, label: "Confirmed", dot: "bg-emerald-500", ring: "border-emerald-200 bg-emerald-50/50" }
                    : t.cleaner_response === "no"
                      ? { color: "red", icon: X, label: "Declined", dot: "bg-red-500", ring: "border-red-200 bg-red-50/50" }
                      : t.cleaner_notified
                        ? { color: "amber", icon: Clock, label: "Awaiting reply", dot: "bg-amber-500", ring: "border-amber-200 bg-amber-50/50" }
                        : { color: "slate", icon: Clock, label: "Not yet sent", dot: "bg-slate-300", ring: "border-slate-200 bg-white" };
                  const Icon = status.icon;
                  return (
                    <div
                      key={t.booking_id}
                      data-testid={`turnover-row-${t.booking_id}`}
                      data-status={t.cleaner_response || (t.cleaner_notified ? "pending" : "unsent")}
                      className={`p-4 rounded-xl border ${status.ring}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">
                            {t.property_name || "Unit"}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 truncate">
                            {t.guest_name} · departs today
                          </div>
                        </div>
                        <span className={`w-2.5 h-2.5 rounded-full mt-1.5 ${status.dot}`} />
                      </div>
                      <div className="ns-hairline my-3" />
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5 text-slate-600 truncate">
                          <Icon className={`w-3.5 h-3.5 text-${status.color}-600`} />
                          <span>
                            {t.cleaner
                              ? `${t.cleaner.first_name} ${t.cleaner.last_name || ""}`.trim()
                              : "No cleaner assigned"}
                          </span>
                        </div>
                        <span className={`text-${status.color}-700 font-medium`}>{status.label}</span>
                      </div>
                      {t.cleaner_response_body && (
                        <div className="mt-2 text-[11px] italic text-slate-500 line-clamp-2">
                          &ldquo;{t.cleaner_response_body}&rdquo;
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="grid lg:grid-cols-2 gap-6">
          <div data-testid="dashboard-survey-tile" className="ns-card dash-wash dash-sun p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="ns-label">Guest feedback</div>
                <div className="font-serif text-xl mt-1">Post-stay surveys</div>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-4 h-4 ${
                      surveys && n <= Math.round(surveys.month_avg)
                        ? "fill-amber-400 text-amber-400"
                        : "text-slate-200"
                    }`}
                  />
                ))}
              </div>
            </div>
            <div className="mt-5 flex items-baseline gap-4">
              <div>
                <div className="font-serif text-5xl text-slate-900 leading-none">
                  {surveys ? surveys.month_avg.toFixed(1) : "—"}
                </div>
                <div className="text-xs text-slate-500 mt-1">avg this month</div>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-serif text-slate-900">
                  {surveys?.month_count ?? 0}
                </div>
                <div className="text-xs text-slate-500 mt-1">responses</div>
              </div>
              <div className="ml-auto text-right">
                <div className="text-2xl font-serif text-slate-900">
                  {surveys?.response_rate ?? 0}%
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  reply rate · {surveys?.total_submitted ?? 0}/{surveys?.total_sent ?? 0}
                </div>
              </div>
            </div>
            <div className="ns-hairline my-5" />
            <div className="ns-label mb-3">Latest comments</div>
            <div className="space-y-3">
              {(!surveys || surveys.latest.length === 0) && (
                <div className="text-sm text-slate-400 py-6 text-center">
                  Nothing back yet — the first surveys go out the day after your next checkout.
                </div>
              )}
              {surveys?.latest.map((s, i) => {
                const bandColor = s.sentiment_band === "green"
                  ? "border-emerald-200 bg-emerald-50/60"
                  : s.sentiment_band === "red"
                    ? "border-red-200 bg-red-50/60"
                    : "border-amber-200 bg-amber-50/60";
                const bandDot = s.sentiment_band === "green"
                  ? "bg-emerald-500"
                  : s.sentiment_band === "red"
                    ? "bg-red-500"
                    : "bg-amber-500";
                return (
                  <div
                    key={i}
                    data-testid={`dashboard-survey-comment-${i}`}
                    data-band={s.sentiment_band}
                    className={`p-3 rounded-xl border ${bandColor}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${bandDot}`} />
                        {s.guest_first_name || "Guest"}
                        <span className="text-slate-400 font-normal">
                          · {s.property_name}
                        </span>
                      </div>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={`w-3.5 h-3.5 ${
                              n <= (s.rating || 0)
                                ? "fill-amber-400 text-amber-400"
                                : "text-slate-200"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    {s.comment && (
                      <div className="text-xs text-slate-600 mt-1.5 italic">
                        &ldquo;{s.comment}&rdquo;
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {surveys?.band_counts && (surveys.band_counts.red + surveys.band_counts.amber + surveys.band_counts.green > 0) && (
              <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  {surveys.band_counts.green} green
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  {surveys.band_counts.amber} amber
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  {surveys.band_counts.red} red
                </span>
              </div>
            )}
          </div>

          <div data-testid="dashboard-referral-tile" className="ns-card dash-wash dash-coral p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="ns-label">Word of mouth</div>
                <div className="font-serif text-xl mt-1">Top referrers</div>
              </div>
              <Share2 className="w-4 h-4 text-teal-700" />
            </div>
            <div className="mt-5 space-y-2">
              {leaderboard.length === 0 && (
                <div className="text-sm text-slate-400 py-8 text-center">
                  No attributed bookings yet — share the referral links from each past
                  guest&apos;s profile to start the flywheel.
                </div>
              )}
              {leaderboard.slice(0, 5).map((r, i) => (
                <Link
                  key={r.contact_id}
                  to={`/contacts/${r.contact_id}`}
                  data-testid={`dashboard-referral-row-${i}`}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-teal-50/50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-serif text-lg">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <div className="text-xs text-slate-500 truncate">{r.email}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-lg text-teal-700 leading-none">
                      {r.referred_bookings}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      bookings · {r.clicks} clicks
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="grid lg:grid-cols-3 gap-6">
          <div data-testid={DASH.upcomingList} className="ns-card dash-wash dash-sea p-6 lg:col-span-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="ns-label">Arriving soon</div>
                <div className="font-serif text-xl mt-1">Upcoming stays</div>
              </div>
              <Link to="/bookings" className="text-xs text-teal-700 hover:underline flex items-center gap-1">
                All bookings <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="mt-5 space-y-3">
              {(stats?.upcoming_bookings || []).length === 0 && (
                <div className="text-sm text-slate-400 py-8 text-center">
                  No upcoming stays yet.
                </div>
              )}
              {(stats?.upcoming_bookings || []).map((b) => (
                <div key={b.id} className="p-3 rounded-xl bg-stone-50 border border-stone-100">
                  <div className="text-sm font-medium">{b.contact_name}</div>
                  <div className="text-xs text-slate-500">
                    {b.property_name} · {b.check_in} → {b.check_out}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div data-testid={DASH.recentContacts} className="ns-card dash-wash dash-teal p-6 lg:col-span-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="ns-label">Fresh entries</div>
                <div className="font-serif text-xl mt-1">Recent contacts</div>
              </div>
              <Link to="/contacts" className="text-xs text-teal-700 hover:underline flex items-center gap-1">
                All contacts <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="mt-5 space-y-3">
              {(stats?.recent_contacts || []).length === 0 && (
                <div className="text-sm text-slate-400 py-8 text-center">
                  Add your first contact to see it here.
                </div>
              )}
              {(stats?.recent_contacts || []).map((c) => (
                <Link
                  to={`/contacts/${c.id}`}
                  key={c.id}
                  className="flex items-center justify-between p-3 rounded-xl hover:bg-stone-50"
                >
                  <div>
                    <div className="text-sm font-medium">
                      {c.first_name} {c.last_name || c.company}
                    </div>
                    <div className="text-xs text-slate-500 capitalize">
                      {c.type} · {c.email || c.phone || "—"}
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-slate-300" />
                </Link>
              ))}
            </div>
          </div>

          <div data-testid={DASH.recentCampaigns} className="ns-card dash-wash dash-coral p-6 lg:col-span-1">
            <div className="flex items-center justify-between">
              <div>
                <div className="ns-label">Recent mail-shots</div>
                <div className="font-serif text-xl mt-1">Campaigns</div>
              </div>
              <Link to="/campaigns" className="text-xs text-teal-700 hover:underline flex items-center gap-1">
                All campaigns <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="mt-5 space-y-3">
              {(stats?.recent_campaigns || []).length === 0 && (
                <div className="text-sm text-slate-400 py-8 text-center flex flex-col items-center gap-2">
                  <Mail className="w-6 h-6 text-slate-300" />
                  <span>No campaigns sent yet.</span>
                </div>
              )}
              {(stats?.recent_campaigns || []).map((c) => (
                <div key={c.id} className="p-3 rounded-xl bg-stone-50 border border-stone-100">
                  <div className="text-sm font-medium">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.subject}</div>
                  <div className="text-[11px] text-teal-700 mt-1">
                    {c.sent_count} sent · {c.failed_count} failed
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
